import shared from 'reducers';
import { get, set } from 'lodash';
import thunk from 'redux-thunk';
import { createStore as createReduxStore, applyMiddleware, combineReducers } from 'redux';

const sharedReducers = shared.reducers;
const sharedActions = shared.actions;

/**
 * Object that will store all the app actions
 * @type {object}
 */
const actions = {};

/**
 * Extend permanently the reducers actions
 * @param {object} customActions - new actions we want make retrievable through the helpers
 * @returns {object}
 */
export function extendActions(customActions) {
    Object.assign(actions, customActions);
    return actions;
}

export default Object.freeze({
    /**
     * Map all the component properties to a certain state
     * @param { Object } props - component properties
     * @returns { Function } function that will be passed to the react-redux connect method
     * @example
     *
     * connect(mapStateToProps({
     *     counter: 'counter',
     *     globalMessage: 'global.message'
     * })(Component)
     */
    mapStateToProps(props) {
        return function(state) {
            return Object.freeze(
                Object.entries(props).reduce((ret, [prop, val]) => {
                    set(ret, prop, get(state, val));
                    return ret;
                }, {}),
            );
        };
    },

    /**
     * Automatically bind the reducers actions to a component
     * @param { Object } props - component properties
     * @param { Object } customActions - custom actions that we could to combine together with the default ones
     * @returns {Function} function that will be passed to the react-redux connect method
     * @example
     *
     * connect(null, mapDispatchToProps({
     *     onClick: 'counter.increase'
     *     onMouseOver: 'counter.decrease',
     *     onChange: 'global.setGlobalMessage',
     * }))(Component)
     *
     * @example
     *
     * connect(null, mapDispatchToProps({
     *     onClick: 'custom.increase'
     *     onMouseOver: 'custom.decrease',
     *     onChange: 'global.setGlobalMessage',
     * }, {
     *   custom: {
     *      increase() {
     *           return { type: 'COUNTER_INCREASE' };
     *      },
     *      decrease() {
     *           return { type: 'COUNTER_DECREASE' };
     *      },
     *   },
     * ))(Component)
     *
     */
    mapDispatchToProps(props, customActions) {
        return function(dispatch) {
            return Object.freeze(
                Object.entries(props).reduce((ret, [prop, val]) => {
                    set(ret, prop, function(...args) {
                        const combinedActions = Object.assign({}, actions, customActions);
                        return dispatch(get(combinedActions, val).apply(null, args));
                    });
                    return ret;
                }, {}),
            );
        };
    },

});

/**
     * Create the application store from combining the local reducers with the custom ones
     * @param {object} customReducers - custom reducers that can add extra data to the app
     * @param {object} customActions - custom actions that will be stored in the actions object to be easily retrived later
     * @param {array}  middlewares - additional middlewares
     * @returns {object} a redux store
     * @example
     *
     * const store = createStore(
     *     combineReducers({ reducer1, reducer2 }), {
     *     custom: {
     *      increase() {
     *           return { type: 'COUNTER_INCREASE' };
     *      },
     *      decrease() {
     *           return { type: 'COUNTER_DECREASE' };
     *      },
     *     }
     *  });
     *
     */

export function createStore(customReducers = {}, customActions = {}, middlewares = []) {
    extendActions(sharedActions);
    extendActions(customActions);

    return createReduxStore(combineReducers(
        Object.assign(
            {},
            sharedReducers,
            customReducers,
        ),
    ), applyMiddleware(thunk, ...middlewares));
};



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/util/redux/index.js